import React, { useEffect, useState } from "react";
import { Hospital, fetchHospitals } from "../../services/hospitals";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";
import { Building2, ChevronDown } from "lucide-react";

interface HospitalSelectorProps {
    selectedHospitalId: string | undefined;
    onHospitalChange: (hospitalId: string | undefined) => void;
}

export function HospitalSelector({ selectedHospitalId, onHospitalChange }: HospitalSelectorProps) {
    const [hospitals, setHospitals] = useState<Hospital[]>([]);
    const [loading, setLoading] = useState(true);

    const { t } = useTranslation();
    const { user } = useAuthStore();

    useEffect(() => {
        const load = async () => {
            // Wait for user to be loaded
            if (!user?.email) return;

            try {
                const list = await fetchHospitals();
                // Defense-in-depth: the /api/hospitals endpoint already scopes the
                // list by role, but for any scoped account (SUPERVISOR and ADMIN
                // "Manager" accounts) we also filter client-side to their assigned
                // hospital_ids so a stale/over-broad response can never surface
                // hospitals outside their assignment. Only DIRECTOR (and an
                // unscoped super-admin, whose hospital_ids is null/empty) is never
                // filtered here — they see everything the API returns.
                const scoped =
                    user.role !== "DIRECTOR" && user.hospital_ids && user.hospital_ids.length > 0
                        ? list.filter((h) => user.hospital_ids!.includes(h.id))
                        : list;
                setHospitals(scoped);
            } catch (err) {
                console.error("Failed to load hospitals", err);
            } finally {
                setLoading(false);
            }
        };
        void load();
    }, [user?.email]);

    if (loading) return <div className="h-10 w-48 animate-pulse rounded-md bg-gray-100" />;

    return (
        <div className="relative inline-block">
            <div className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                <Building2 className="h-4 w-4 text-gray-500" />
                <select
                    value={selectedHospitalId || ""}
                    onChange={(e) => onHospitalChange(e.target.value || undefined)}
                    className="appearance-none bg-transparent pr-8 focus:outline-none"
                >
                    <option value="">{t("home.allHospitalsAggregated", "All Hospitals (Aggregated)")}</option>
                    {hospitals.map((h) => (
                        <option key={h.id} value={h.id}>
                            {h.name}
                        </option>
                    ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                    <ChevronDown className="h-4 w-4" />
                </div>
            </div>
        </div>
    );
}
